import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, concatMap, switchMap, tap } from 'rxjs/operators';
import { Observable, EMPTY, of } from 'rxjs';
import { RolesService } from '@app/services/roles/roles.service';
import * as RoleActions from '../actions/role.actions';
import { JwtService } from '@app/core/services/jwt.service';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DeleteRoleModalComponent } from '../../components/delete-role-modal/delete-role-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { ConfirmModalComponent } from '@app/shared/components/confirm-modal/confirm-modal.component';
import { TranslateService } from '@ngx-translate/core';
import * as CoreActions from '@app/core/store/core.action';

@Injectable()
export class RoleEffects {

  loadRoles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoleActions.loadRoles),
      switchMap(() => {
        return this._roles.onGetRoles('roleNameEn', 'asc').pipe(
          map(({ data })=> RoleActions.loadRolesSuccess({ roles: data.roles, total: data.total })),
          catchError(error => of(RoleActions.loadRolesFailure({ error }))))
      }
      )
    );
  });

  loadRole$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoleActions.loadRole),
      switchMap((action) => {
     
        return this._roles.onGetRole(action.id).pipe(
          map(role => RoleActions.loadRoleSuccess({ role })),
          catchError(error => of(RoleActions.loadRoleFailure({ error }))))
      }
      )
    );
  });

  saveRole$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoleActions.saveRole),
      switchMap((action) => {
        return this._jwt.onSaveRole(action).pipe(
          map(({ role }) => {
            return RoleActions.saveRoleSuccess({ role: role})
          }),
          catchError(error => of(RoleActions.saveRoleFailure({ error }))))
      }
      )
    );
  });

  updateRole$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoleActions.updateRole),
      switchMap((action) => {
        return this._roles.onUpdateRole(action.id, action.role).pipe(
          map(({ role }) => {
            return RoleActions.updateRoleSuccess({ role: role })
          }),
          catchError(error => of(RoleActions.updateRoleFailture({ error }))))
      }
      )
    );
  });

  deleteRole$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoleActions.deleteRole),
      switchMap((action) => {
        return this._roles.onDeleteRole(action.id).pipe(
          // tap(() => this._toastr.error(`Successfully deleted role.`)),
          map((item: any) => {
              // A refused delete still comes back as 200; keep the row instead of
              // dropping it from the list as if it had been deleted.
              if (this.isAccessDenied(item)) {
                return this.permissionMessage();
              }
              return RoleActions.deleteRoleSuccess({ id: action.id })
          }),
          catchError(error => of(RoleActions.loadRolesFailure({ error }))))
      }
      )
    );
  });


  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoleActions.sortRoles),
      switchMap((action) => {
        return this._roles.onGetRoles(action.sort, action.direction).pipe(
          map(({ data })=> RoleActions.loadRolesSuccess({ roles: data.roles, total: data.total })),
          catchError(error => of(RoleActions.loadRolesFailure({ error }))))
      }
      )
    );
  });
  
  DeleteRoleShowErrorMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RoleActions.DeleteRoleShowErrorMessage),
      tap((action) => {
        const role = this._roles.onGetRole(action.current_role_id).subscribe((role) => {
        
          const rolelist = this._roles.onGetRoles('roleNameEn', 'asc').subscribe(rolelist => {
            const filteredtolelist = rolelist.data.roles.filter(x => x.roleID != action.current_role_id);
    
            if(role.Users && role.Users.length>0){
              
              action.current_role_id = action.current_role_id;
              action.role_list = filteredtolelist;
              action.user_count = role.Users.length;
              const ref = this.dialog.open(DeleteRoleModalComponent, {
                minWidth: '30vw',
                maxWidth: '700px',
                data: {
                  user_count: role.Users.length,
                  current_role_id: action.current_role_id,
                  role_list: action.role_list,
                }
              });

            }
            else{
              
              const ref = this.dialog.open(ConfirmModalComponent, {
                minWidth:'30vw',
                maxWidth: '700px',
                data:{
                  key: `role`,
                  deleteMessage: `${this._translation.instant('deleteConfirmSimple')}`,
                  deletePassword: 'DELETE'
                },
              });
          
          
              ref.afterClosed().subscribe((res) => {
                if(res == 'true'){
                  this.store$.dispatch(RoleActions.deleteRole({ id:action.current_role_id }));
                }
              });

            }
          });
        });
        /**/
      })
    ),
    { dispatch: false }
  );

  assignNewRoles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(RoleActions.AssignNewRoles),
      switchMap((action) => {
        return this._roles.onAssignNewRoles(action.current_role_id,action.new_role_ids).pipe(
          map((item: any) => {
            if (this.isAccessDenied(item)) {
              return this.permissionMessage();
            }
            this._toastr.error(`${this._translation.instant('successfullyDeletedRole')}`);
            return RoleActions.deleteRoleSuccess({ id: action.current_role_id })
        }),
          catchError(error => of(RoleActions.loadRolesFailure({ error }))))
      }
      )
    );
  });
  
  private isAccessDenied(response: any): boolean {
    return response?.result === 'failed.access';
  }

  private permissionMessage() {
    return CoreActions.showPermissionMessage({
      title: this._translation.instant('errormessage.warning'),
      body: this._translation.instant('errormessage.access')
    });
  }

  constructor(private actions$: Actions,private store$: Store, private dialog: MatDialog, private _roles: RolesService, private _jwt: JwtService, private _router: Router, private _toastr: ToastrService, private _translation: TranslateService) { }

}
